import {
  Bell,
  Calendar,
  ChevronDown,
  Eye,
  EyeOff,
  Globe,
  Key,
  Lock,
  Mail,
  MessageSquare,
  Pill,
  Plane,
  Palette,
  Settings,
  Shield,
  Smartphone,
} from "lucide-react";
import { PartnerOffersSettingsCard } from "../../components/settings/PartnerOffersSettingsCard";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient from "../../services/api/apiClient";
import { useAuthStore } from "../../stores/authStore";
import { PageBanner } from "../../components/ui/PageBanner";
import SecondFactorCard from "../../components/settings/SecondFactorCard";
import { ThemeSettingsCard } from "../../components/settings/ThemeSettingsCard";
import { OrientationSettingsCard } from "@/components/settings/OrientationSettingsCard";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import { useI18nStore } from "../../stores/i18nStore";
import { Language, Translation } from "../../types/i18n";
const TABS = ["general", "security", "appearance"] as const;
type SettingsTab = (typeof TABS)[number];

const TAB_META: Record<
  SettingsTab,
  {
    icon: typeof Settings;
    title: (t: Translation) => string;
    description: (t: Translation) => string;
  }
> = {
  general: {
    icon: Bell,
    title: (t) => t.navNotifications,
    description: (t) => t.settingsNotificationsDescription,
  },
  security: {
    icon: Shield,
    title: (t) => t.security,
    description: (t) => t.settingsSecurityDescription,
  },
  appearance: {
    icon: Palette,
    title: (t) => t.appearance,
    description: (t) => t.settingsAppearanceDescription,
  },
};

function isSettingsTab(value: string | null): value is SettingsTab {
  return TABS.includes((value ?? "") as SettingsTab);
}

/** What to tell the user when the change was refused. */
function passwordChangeError(error: unknown, t: Translation): string {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { error?: { code?: string; message?: string } };
      };
    } | null
  )?.response;
  const code = response?.data?.error?.code;
  if (code === "INVALID_CURRENT_PASSWORD") {
    return t.settings2PasswordCurrentWrong;
  }
  // The backend's own wording names the rule that was broken.
  if (code === "WEAK_PASSWORD" || code === "PASSWORD_UNCHANGED") {
    return response?.data?.error?.message ?? t.settings2PasswordNotAllowed;
  }
  if (response?.status === 429) {
    return t.settings2PasswordTooManyAttempts;
  }
  return t.settings2PasswordChangeFailed;
}

export const SettingsPage: React.FC = () => {
  const { t, language, setLanguage, getLanguageFlag, getLanguageName } =
    useI18nStore();
  const [searchParams] = useSearchParams();
  // An unknown or absent ?tab falls back to general rather than rendering
  // nothing, so an old bookmark still lands somewhere useful.
  const requested = searchParams.get("tab");
  const tab: SettingsTab = isSettingsTab(requested) ? requested : "general";
  const {
    isSupported: pushSupported,
    isSubscribed,
    permission: pushPermission,
    isLoading: pushLoading,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
  } = usePushNotifications();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: false,
    medicationReminders: true,
    travelReminders: true,
    healthCheckReminders: false,
  });

  const languages: Language[] = ["de", "en", "fr", "es", "pt"];

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
    setShowLanguageDropdown(false);
  };

  const handlePasswordChange = async () => {
    // Never log `passwordData`: it holds the current and the new password in
    // clear text (audit H10).
    setPasswordError(null);
    setPasswordSaving(true);
    try {
      await apiClient.post("/auth/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
    } catch (error) {
      setPasswordError(passwordChangeError(error, t));
      setPasswordSaving(false);
      return;
    }

    // The backend ends every session, this one included, so staying on the
    // page would only lead to the next request failing. Sign out properly -
    // that also clears the health data this device holds - and go to the
    // login page, where the new password is the one that works.
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    toast.success(t.settings2PasswordChanged);
    await logout();
    navigate("/login", { replace: true });
  };

  const handleNotificationToggle = (
    setting: keyof typeof notificationSettings,
  ) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [setting]: !prev[setting],
    }));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <PageBanner
        icon={TAB_META[tab].icon}
        title={TAB_META[tab].title(t)}
        description={TAB_META[tab].description(t)}
      />

      {/* No tab bar: Settings, Security and Appearance are reached from the
          account menu, which links here with ?tab=..., and a second row of
          tabs only repeated it. The banner names the section that is open. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {tab === "security" && (
            <>
              {/* Account Security */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span>{t.settings2AccountSecurity}</span>
                </h2>

                {/* Reads the account's real second factor and manages passkeys.
                    What was here before was a fixed green "Enabled" badge that
                    said the same thing for every account, protected or not. */}
                <SecondFactorCard />

                {/* Password Change */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <Key className="w-4 h-4" />
                    <span>{t.changePassword}</span>
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="setting-current-password"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {t.currentPassword}
                      </label>
                      <div className="relative">
                        <input
                          id="setting-current-password"
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) =>
                            setPasswordData((prev) => ({
                              ...prev,
                              currentPassword: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                          placeholder={t.settings2CurrentPasswordPlaceholder}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="setting-new-password"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {t.newPassword}
                      </label>
                      <div className="relative">
                        <input
                          id="setting-new-password"
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) =>
                            setPasswordData((prev) => ({
                              ...prev,
                              newPassword: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                          placeholder={t.settings2NewPasswordPlaceholder}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showNewPassword ? (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="setting-confirm-password"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {t.confirmNewPassword}
                      </label>
                      <div className="relative">
                        <input
                          id="setting-confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) =>
                            setPasswordData((prev) => ({
                              ...prev,
                              confirmPassword: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                          placeholder={t.settings2RepeatPasswordPlaceholder}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    {passwordData.confirmPassword !== "" &&
                      passwordData.confirmPassword !==
                        passwordData.newPassword && (
                        <p className="text-sm text-gray-600">
                          {t.settings2PasswordsDoNotMatch}
                        </p>
                      )}

                    {passwordError && (
                      <p
                        role="alert"
                        className="text-sm text-red-600"
                        data-testid="password-change-error"
                      >
                        {passwordError}
                      </p>
                    )}

                    <button
                      onClick={() => void handlePasswordChange()}
                      disabled={
                        passwordSaving ||
                        !passwordData.currentPassword ||
                        !passwordData.newPassword ||
                        passwordData.newPassword !==
                          passwordData.confirmPassword
                      }
                      className="flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      <Lock className="w-4 h-4" />
                      <span>{t.changePassword}</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {tab === "appearance" && (
            <>
              {/* Language Settings */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-purple-600" />
                  <span>{t.languageSettings}</span>
                </h2>

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="language-select"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      {t.settings2PortalLanguage}
                    </label>
                    <div className="relative">
                      <button
                        id="language-select"
                        onClick={() =>
                          setShowLanguageDropdown(!showLanguageDropdown)
                        }
                        className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <Globe className="w-5 h-5 text-gray-500" />
                          <span className="text-2xl">
                            {getLanguageFlag(language)}
                          </span>
                          <span className="font-medium text-gray-900">
                            {getLanguageName(language)}
                          </span>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-400 transition-transform ${showLanguageDropdown ? "rotate-180" : ""}`}
                        />
                      </button>

                      {showLanguageDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                          <div className="py-1">
                            {languages.map((lang) => (
                              <button
                                key={lang}
                                onClick={() => handleLanguageChange(lang)}
                                className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                                  language === lang
                                    ? "bg-sky-50 text-sky-700"
                                    : "text-gray-900"
                                }`}
                              >
                                <span className="text-2xl">
                                  {getLanguageFlag(lang)}
                                </span>
                                <span className="font-medium">
                                  {getLanguageName(lang)}
                                </span>
                                {language === lang && (
                                  <div className="ml-auto w-2 h-2 bg-sky-500 rounded-full"></div>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      {t.settings2PortalLanguageHint}
                    </p>
                  </div>
                </div>
              </div>
              <ThemeSettingsCard />
              <OrientationSettingsCard />
            </>
          )}

          {tab === "general" && (
            <>
              {/* Notification Settings */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                  <Bell className="w-5 h-5 text-blue-600" />
                  <span>{t.settings2Notifications}</span>
                </h2>

                <div className="space-y-6">
                  {/* General Notifications */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t.settings2GeneralNotifications}
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Mail className="w-5 h-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-700 block">
                              {t.settings2EmailNotifications}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t.notificationsEmailHint}
                            </span>
                          </div>
                        </div>
                        <label
                          htmlFor="email-notifications-toggle"
                          className="relative inline-flex items-center cursor-pointer"
                          aria-label={t.settings2EmailNotifications}
                        >
                          <input
                            id="email-notifications-toggle"
                            type="checkbox"
                            checked={notificationSettings.emailNotifications}
                            onChange={() =>
                              handleNotificationToggle("emailNotifications")
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Smartphone className="w-5 h-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-700 block">
                              {t.settings2SmsNotifications}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t.settings2SmsNotificationsHint}
                            </span>
                          </div>
                        </div>
                        <label
                          htmlFor="sms-notifications-toggle"
                          className="relative inline-flex items-center cursor-pointer"
                          aria-label={t.settings2SmsNotifications}
                        >
                          <input
                            id="sms-notifications-toggle"
                            type="checkbox"
                            checked={notificationSettings.smsNotifications}
                            onChange={() =>
                              handleNotificationToggle("smsNotifications")
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <MessageSquare className="w-5 h-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-700 block">
                              {t.settings2PushNotifications}
                            </span>
                            <span className="text-xs text-gray-500">
                              {!pushSupported
                                ? t.settings2PushUnsupported
                                : pushPermission === "denied"
                                  ? t.settings2PushBlocked
                                  : isSubscribed
                                    ? t.settings2PushActive
                                    : t.settings2PushEnableHint}
                            </span>
                          </div>
                        </div>
                        {pushSupported && pushPermission !== "denied" ? (
                          <button
                            type="button"
                            disabled={pushLoading}
                            onClick={
                              isSubscribed ? unsubscribePush : subscribePush
                            }
                            aria-label={t.settings2PushNotifications}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                              isSubscribed ? "bg-sky-600" : "bg-gray-200"
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                isSubscribed ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 italic">
                            {!pushSupported
                              ? t.settings2PushUnavailableShort
                              : t.settings2PushBlockedShort}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Specific Reminders */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t.remindersTitle}
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Pill className="w-5 h-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-700 block">
                              {t.settings2MedicationReminders}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t.settings2MedicationRemindersHint}
                            </span>
                          </div>
                        </div>
                        <label
                          htmlFor="medication-reminders-toggle"
                          className="relative inline-flex items-center cursor-pointer"
                          aria-label={t.settings2MedicationReminders}
                        >
                          <input
                            id="medication-reminders-toggle"
                            type="checkbox"
                            checked={notificationSettings.medicationReminders}
                            onChange={() =>
                              handleNotificationToggle("medicationReminders")
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Plane className="w-5 h-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-700 block">
                              {t.settings2TravelReminders}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t.settings2TravelRemindersHint}
                            </span>
                          </div>
                        </div>
                        <label
                          htmlFor="travel-reminders-toggle"
                          className="relative inline-flex items-center cursor-pointer"
                          aria-label={t.settings2TravelReminders}
                        >
                          <input
                            id="travel-reminders-toggle"
                            type="checkbox"
                            checked={notificationSettings.travelReminders}
                            onChange={() =>
                              handleNotificationToggle("travelReminders")
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Calendar className="w-5 h-5 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium text-gray-700 block">
                              {t.settings2HealthCheckReminders}
                            </span>
                            <span className="text-xs text-gray-500">
                              {t.settings2HealthCheckRemindersHint}
                            </span>
                          </div>
                        </div>
                        <label
                          htmlFor="health-check-reminders-toggle"
                          className="relative inline-flex items-center cursor-pointer"
                          aria-label={t.settings2HealthCheckReminders}
                        >
                          <input
                            id="health-check-reminders-toggle"
                            type="checkbox"
                            checked={notificationSettings.healthCheckReminders}
                            onChange={() =>
                              handleNotificationToggle("healthCheckReminders")
                            }
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Partner offers on trips, to show or hide (REQ-025 AC-008) */}
              <PartnerOffersSettingsCard />
            </>
          )}
        </div>

        <div className="space-y-6">
          {tab === "security" && (
            <>
              {/* Privacy Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <h3 className="text-green-800 font-medium text-sm">
                      {t.settings2PrivacySecurity}
                    </h3>
                    <p className="text-green-700 text-xs mt-1">
                      {t.settings2PrivacySecurityText}
                    </p>
                    <p className="text-green-700 text-xs mt-1">
                      <strong>{t.settings2StandardisedFormat}</strong>{" "}
                      {t.fhirStandardNote}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
